import { Note } from "tonal";

/** Valid MIDI note range (MIDI 1.0). */
export const MIDI_MIN = 0;
export const MIDI_MAX = 127;

export type Accidentals = "sharps" | "flats";

const SHARP_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"] as const;
const FLAT_NAMES = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"] as const;

export function isValidMidiNote(n: number): boolean {
  return Number.isInteger(n) && n >= MIDI_MIN && n <= MIDI_MAX;
}

/** Pitch class 0–11 for any integer (handles negatives). */
export function pitchClass(n: number): number {
  return ((n % 12) + 12) % 12;
}

/** Octave in scientific pitch notation (MIDI 60 = C4). */
export function octaveOf(midi: number): number {
  return Math.floor(midi / 12) - 1;
}

export function pitchClassName(pc: number, accidentals: Accidentals = "sharps"): string {
  const names = accidentals === "flats" ? FLAT_NAMES : SHARP_NAMES;
  return names[pitchClass(pc)]!;
}

/** Name with octave, e.g. 61 → "C#4" (or "Db4" with flats). */
export function midiToName(midi: number, accidentals: Accidentals = "sharps"): string {
  return `${pitchClassName(midi, accidentals)}${octaveOf(midi)}`;
}

/** Parse "C4", "F#3", "Bb2"… → MIDI number, or null if invalid / out of range. */
export function nameToMidi(name: string): number | null {
  const m = Note.midi(name);
  return m !== null && isValidMidiNote(m) ? m : null;
}

/** Pitch class of a note name without octave ("Gb" → 6), or null. */
export function nameToPitchClass(name: string): number | null {
  const chroma = Note.chroma(name);
  return chroma === undefined || Number.isNaN(chroma) ? null : chroma;
}

export interface TransposeResult {
  note: number | null; // null when the result leaves 0–127
  outOfRange: boolean;
}

/** Transpose a MIDI note, reporting (not clamping) out-of-range results. */
export function transposeMidi(midi: number, semitones: number): TransposeResult {
  const n = midi + semitones;
  return isValidMidiNote(n) ? { note: n, outOfRange: false } : { note: null, outOfRange: true };
}

const INTERVAL_NAMES = [
  "unison", "minor second", "major second", "minor third", "major third", "perfect fourth",
  "tritone", "perfect fifth", "minor sixth", "major sixth", "minor seventh", "major seventh",
] as const;

const INTERVAL_SHORT = ["P1", "m2", "M2", "m3", "M3", "P4", "TT", "P5", "m6", "M6", "m7", "M7"] as const;

/**
 * Interval name by semitone distance. Enharmonic spelling (e.g. augmented
 * fourth vs diminished fifth) is not distinguished — named by distance.
 */
export function intervalName(semitones: number): string {
  const abs = Math.abs(semitones);
  if (abs === 12) return "octave";
  const simple = INTERVAL_NAMES[abs % 12]!;
  const octaves = Math.floor(abs / 12);
  if (octaves === 0) return simple;
  return `${simple} + ${octaves} octave${octaves > 1 ? "s" : ""}`;
}

export function intervalShortName(semitones: number): string {
  const abs = Math.abs(semitones);
  if (abs === 12) return "P8";
  return INTERVAL_SHORT[abs % 12]! + (abs > 12 ? `+${Math.floor(abs / 12)}oct` : "");
}
